import React, {
  createContext,
  useContext,
  ReactNode,
  useState,
  useEffect,
} from 'react';
import { useParams } from 'react-router-dom';
import { CommonCropPledgeCreditorInformationSerializer } from '@bcba/serializers';
import {
  QueryObserverResult,
  RefetchOptions,
  UseMutateAsyncFunction,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import { useCropPledgeContext } from '../../context/CropPledgeContext';
import {
  CROP_PLEDGE_USER_ROLES,
  CropPledgeStatusSummarySectionResponse,
  CropPledgeUserRole,
  MARITAL_STATUSES,
  MaritalStatus,
  NATIONALITIES,
  NationalityType,
  PLEDGE_SECTION_STATUSES,
} from '@bcba/types';
import { BadgeColorScheme } from '@agrotoken-tech/ui';
import { useToastsHook, useTranslation } from '@bcba/react-hooks';
import {
  getCropPledgeCreditorInformation,
  patchApproveCropPledgeCreditorInformation,
  patchCropPledgeCreditorInformationSetCreditor,
} from '../../../../../services/crop-pledge';
import { CommonUpdateCropPledgeCreditorInformationDto } from '../../../../../services/crop-pledge/dto/update';

type CropPledgeCreditorContextType = {
  isLoadingCreditorInformation: boolean;
  cropPledgeCreditorInformation?: CommonCropPledgeCreditorInformationSerializer;
  refetchCropPledgeCreditorInformation: (
    options?: RefetchOptions,
  ) => Promise<
    QueryObserverResult<CommonCropPledgeCreditorInformationSerializer, Error>
  >;
  cropPledgeSectionsStatusBadgeColors: Record<
    (typeof PLEDGE_SECTION_STATUSES)[keyof typeof PLEDGE_SECTION_STATUSES],
    BadgeColorScheme
  >;
  role: CropPledgeUserRole;
  creditorSectionStatus?: CropPledgeStatusSummarySectionResponse;
  selectedCreditorData?: CommonCropPledgeCreditorInformationSerializer;
  setSelectedCreditorData: React.Dispatch<
    React.SetStateAction<
      CommonCropPledgeCreditorInformationSerializer | undefined
    >
  >;
  nationalitiesOptions: { label: string; value: NationalityType }[];
  maritalStatusOptions: { label: string; value: MaritalStatus }[];
  setCreditorInfo: UseMutateAsyncFunction<
    void,
    Error,
    {
      fiscalId: string;
    },
    unknown
  >;
  isPendingSetCreditorInfo: boolean;
  approveSection: UseMutateAsyncFunction<
    CommonCropPledgeCreditorInformationSerializer,
    Error,
    CommonUpdateCropPledgeCreditorInformationDto,
    unknown
  >;
  isPendingSectionApproval: boolean;
  isFormReadOnly: boolean;
};

const CropPledgeCreditorContext = createContext<
  CropPledgeCreditorContextType | undefined
>(undefined);
type CropPledgeCreditorProviderProps = {
  children: ReactNode;
};

export const CropPledgeCreditorProvider: React.FC<
  CropPledgeCreditorProviderProps
> = ({ children }) => {
  const { t } = useTranslation();
  const params = useParams();
  const cropPledgeUuid = params['uuid'] || '';
  const {
    cropPledgeSectionsStatusBadgeColors,
    role,
    cropPledgeStatus,
    refetchCropPledgeStatus,
  } = useCropPledgeContext();
  const { showSuccessToast } = useToastsHook();
  const { isPledgeInAnEditableStage } = useCropPledgeContext();

  const { sections } = cropPledgeStatus ?? {};
  const { creditor: creditorSectionStatus } = sections ?? {};

  const [selectedCreditorData, setSelectedCreditorData] =
    useState<CommonCropPledgeCreditorInformationSerializer>();

  const {
    data: cropPledgeCreditorInformation,
    isLoading: isLoadingCreditorInformation,
    refetch: refetchCropPledgeCreditorInformation,
  } = useQuery({
    queryKey: ['get-crop-pledge-creditor-information', cropPledgeUuid],
    queryFn: () => getCropPledgeCreditorInformation(cropPledgeUuid),
  });

  useEffect(() => {
    if (cropPledgeCreditorInformation?.fiscalId) {
      setSelectedCreditorData(cropPledgeCreditorInformation);
    }
  }, [cropPledgeCreditorInformation]);

  const nationalitiesOptions = Object.values(NATIONALITIES).map((value) => ({
    label: t(`nationality.${value}`),
    value,
  }));

  const maritalStatusOptions = Object.values(MARITAL_STATUSES).map((value) => ({
    label: t(`common.maritalStatus.${value}`),
    value,
  }));

  const { mutateAsync: setCreditorInfo, isPending: isPendingSetCreditorInfo } =
    useMutation({
      mutationFn: (payload: { fiscalId: string }) =>
        patchCropPledgeCreditorInformationSetCreditor(cropPledgeUuid, payload),
      onSuccess: () => {
        refetchCropPledgeCreditorInformation();
        refetchCropPledgeStatus();
        showSuccessToast({ title: t('common.requestApproval.toast.success') });
      },
    });

  const { mutateAsync: approveSection, isPending: isPendingSectionApproval } =
    useMutation({
      mutationFn: (payload: CommonUpdateCropPledgeCreditorInformationDto) =>
        patchApproveCropPledgeCreditorInformation(cropPledgeUuid, payload),
      onSuccess: () => {
        refetchCropPledgeCreditorInformation();
        refetchCropPledgeStatus();
        showSuccessToast({ title: t('common.approve.toast.success') });
      },
    });

  const isFormReadOnly =
    !isPledgeInAnEditableStage ||
    role === CROP_PLEDGE_USER_ROLES.DEBTOR ||
    (role === CROP_PLEDGE_USER_ROLES.CREDITOR &&
      creditorSectionStatus?.status !==
        PLEDGE_SECTION_STATUSES.PENDING_CREDITOR_LOAD &&
      creditorSectionStatus?.status !==
        PLEDGE_SECTION_STATUSES.PENDING_CREDITOR_APPROVAL);

  return (
    <CropPledgeCreditorContext.Provider
      value={{
        isLoadingCreditorInformation,
        cropPledgeCreditorInformation,
        refetchCropPledgeCreditorInformation,
        cropPledgeSectionsStatusBadgeColors,
        role,
        creditorSectionStatus,
        selectedCreditorData,
        setSelectedCreditorData,
        nationalitiesOptions,
        maritalStatusOptions,
        setCreditorInfo,
        isPendingSetCreditorInfo,
        approveSection,
        isPendingSectionApproval,
        isFormReadOnly,
      }}
    >
      {children}
    </CropPledgeCreditorContext.Provider>
  );
};

export const useCropPledgeCreditorContext =
  (): CropPledgeCreditorContextType => {
    const context = useContext(CropPledgeCreditorContext);
    if (context === undefined) {
      throw new Error(
        'useCropPledgeCreditorContext must be used within a CropPledgeCreditorProvider',
      );
    }
    return context;
  };
